import { CustomEventWithDetail, html, View } from 'rune-ts';
import klass from '../CategoryAccordion.module.scss';
import { typo } from '../../../../../shared/typography/typo';
import { CateListTypeS } from '../../../../../features/CateList/outbound/share';

export interface CategoryAccordionItemOption {
  klass?: string;
  is_opened?: boolean;
  active_cate_item_id?: number;
}

export type CategoryAccordionItemData = Pick<CateListTypeS.LegacyCateList, '_' | 'name' | 'name_en'> & {
  id?: number;
};

export class CategoryAccordionItemChangeEvent extends CustomEventWithDetail<{
  cate_list_id?: number;
  cate_item_id?: number;
}> {}

export class CategoryAccordionItem extends View<CategoryAccordionItemData> {
  state: CategoryAccordionItemOption & {
    has_child: boolean;
  };

  constructor(data: CategoryAccordionItemData, option: CategoryAccordionItemOption = {}) {
    super(data);

    this.state = {
      klass: option.klass ?? '',
      is_opened: option.is_opened ?? false,
      active_cate_item_id: option.active_cate_item_id,
      has_child: !!this.data._.cate_items.length,
    };

    this.setDefaultCateItemId();
  }

  protected override onRender() {
    this.delegate('click', `.${klass.title}`, (e) => {
      if (this.state.is_opened) {
        return;
      }
      this.dispatchEvent(CategoryAccordionItemChangeEvent, {
        bubbles: true,
        detail: {
          cate_list_id: this.data.id,
          cate_item_id: undefined,
        },
      });
    });

    this.delegate('click', `.${klass.cate_item}`, (e) => {
      const el = e.currentTarget as HTMLElement;

      const parseCateItemId = () => {
        if (!el.dataset.cate_item_id) {
          return undefined;
        }
        const parsed = parseInt(el.dataset.cate_item_id, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      };

      const cate_item_id = parseCateItemId();
      this.dispatchEvent(CategoryAccordionItemChangeEvent, {
        bubbles: true,
        detail: {
          cate_list_id: this.data.id,
          cate_item_id,
        },
      });
      this.state.active_cate_item_id = cate_item_id;
      this.redraw();
    });
  }

  override redraw() {
    if (this.state.is_opened) {
      this.element().classList.add(klass.opened);
    } else {
      this.element().classList.remove(klass.opened);
    }

    this.element()
      .querySelectorAll(`.${klass.cate_item}`)
      .forEach((cate_item_el) => {
        const el = cate_item_el as HTMLElement;
        if (el.dataset.cate_item_id === this.state.active_cate_item_id?.toString()) {
          el.classList.add(klass.active);
          return;
        }
        el.classList.remove(klass.active);
      });

    return this;
  }

  override template() {
    const { cate_items } = this.data._;
    const { is_opened, active_cate_item_id } = this.state;
    return html`
      <div class="${klass.cate_list} ${is_opened ? klass.opened : ''}">
        <div class="${klass.title}">
          <button class="${klass.name} ${klass.bold} ${typo('16_bold')}">${this.data.name}</button>
          <button class="${klass.name} ${klass.medium} ${typo('16_medium')}">${this.data.name}</button>
        </div>

        ${cate_items.length
          ? html`
              <ul class="${klass.cate_items}">
                <!-- 전체 -->
                ${cate_items.length > 1
                  ? html`<li
                      data-cate_list_id="${this.data.id}"
                      class="${klass.cate_item} ${active_cate_item_id === undefined ? klass.active : ''}"
                    >
                      <button class="${klass.name} ${klass.bold} ${typo('14_bold')}">
                        ${ET('mps2::product::all')}
                      </button>
                      <button class="${klass.name} ${klass.medium} ${typo('14_medium')}">
                        ${ET('mps2::product::all')}
                      </button>
                    </li>`
                  : ''}

                <!-- 중카테고리 -->
                ${cate_items.map((cate_item) => {
                  return html`<li
                    data-cate_list_id="${this.data.id}"
                    data-cate_item_id="${cate_item.id}"
                    class="${klass.cate_item} ${active_cate_item_id === cate_item.id ? klass.active : ''}"
                  >
                    <button class="${klass.name} ${klass.bold} ${typo('14_bold')}">${cate_item.name}</button>
                    <button class="${klass.name} ${klass.medium} ${typo('14_medium')}">
                      ${cate_item.name}
                    </button>
                  </li>`;
                })}
              </ul>
            `
          : ''}
      </div>
    `;
  }

  open() {
    this.state.is_opened = true;
  }

  close() {
    this.state.is_opened = false;
    this.state.active_cate_item_id = undefined;
  }

  private setDefaultCateItemId(): number | undefined {
    if (this.state.active_cate_item_id) {
      return;
    }

    const cate_items = this.data._.cate_items;
    // 중카테고리가 1개이면 "전체" 중카테고리를 숨긴다.
    // 따라서 첫 번째 중카테고리가 active 된다.
    if (cate_items.length === 1) {
      this.state.active_cate_item_id = cate_items[0].id;
    } else {
      this.state.active_cate_item_id = undefined;
    }
  }
}
